import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  memo,
} from "react";
import InputSearch from "../InputSearch/InputSearch";
import { AggregateUser, Role, User } from "@/gql/graphql";
import { useLazyQuery } from "@apollo/client";
import { useSearchContext } from "@/contexts/SearchContext";
import useLang from "@/hooks/useLang";
import { DrawerDialog } from "@/components/custom-ui/DrawerDialog";
import { InView } from "react-intersection-observer";
import SkeletonPost from "@/components/feed/SkeletonPost/SkeletonPost";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import dynamic from "next/dynamic";
import { GET_CRAFTMEN_FEED } from "@/graphql/feed";

const PopoverFilter = dynamic(() => import("../PopoverFilter/PopoverFilter"), {
  ssr: false,
  loading: () => null, // Pas de loader, apparait instantanément
});

const PostCard = dynamic(() => import("@/components/feed/PostCard/PostCard"), {
  ssr: false,
  loading: () => <SkeletonPost />, // Skeleton pendant le chargement
});

interface ModalSearchProps {
  openModalSearch: boolean;
  setOpenModalSearch: Dispatch<SetStateAction<boolean>>;
}

// Memoized PostCard wrapper pour éviter les re-renders inutiles
const MemoizedPostCard = memo(
  ({ user }: { user: User }) => <PostCard user={user} />,
  (prevProps, nextProps) => prevProps.user.id === nextProps.user.id
);

MemoizedPostCard.displayName = "MemoizedPostCard";

const ModalSearch = ({
  openModalSearch,
  setOpenModalSearch,
}: ModalSearchProps) => {
  // Constants
  const LOAD_MORE_THRESHOLD = 0.75;
  const DEBOUNCE_DELAY = 300;
  const SEARCH_DEBOUNCE = 500;

  // Refs
  const loadingRef = useRef(false);
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const loadMoreDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const lastQueryRef = useRef<string>("");
  const scrollPositionRef = useRef<number>(0);

  // Hooks
  const { getText } = useLang();
  const isMobileXs = useMediaQuery("(max-width: 350px)");

  // Detect iOS (memoized pour éviter recalcul)
  const isIos = useMemo(
    () =>
      typeof window !== "undefined" &&
      typeof navigator !== "undefined" &&
      /iPhone|iPad|iPod/i.test(navigator.userAgent),
    []
  );

  // Search context
  const {
    searchTerm,
    setSearchTerm,
    selectedSegmentations,
    setSelectedSegmentations,
    selectedCategories,
    setSelectedCategories,
    selectedCountries,
    setSelectedCountries,
    selectedItems,
    setSelectedItems,
    selectedMaterials,
    setSelectedMaterials,
    loadingMore,
    setLoadingMore,
    hasMore,
    setHasMore,
    size,
    orderBy,
    query,
  } = useSearchContext();

  // GraphQL query avec optimisations
  const [getCraftmans, { loading, data, fetchMore, refetch, error }] =
    useLazyQuery<{
      users: User[];
      aggregateUser: AggregateUser;
    }>(GET_CRAFTMEN_FEED, {
      variables: {
        ...orderBy,
        skip: 0,
        take: size,
        where: {
          ...query,
          role: { equals: Role.Craftman },
          active: { equals: true },
        },
        aggregateUserWhere2: {
          ...query,
        },
      },
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
      // Optimisations Apollo
      returnPartialData: true,
      nextFetchPolicy: "cache-first",
    });

  // Count active filters (memoized)
  const countFilter = useMemo(() => {
    return (
      selectedCountries.length +
      selectedItems.length +
      selectedCategories.length +
      selectedMaterials.length +
      selectedSegmentations.length
    );
  }, [
    selectedCountries.length,
    selectedItems.length,
    selectedCategories.length,
    selectedMaterials.length,
    selectedSegmentations.length,
  ]);

  // Clear all filters (stable reference)
  const clearFilters = useCallback(() => {
    setSelectedSegmentations([]);
    setSelectedCategories([]);
    setSelectedMaterials([]);
    setSelectedItems([]);
    setSelectedCountries([]);
  }, [
    setSelectedCategories,
    setSelectedCountries,
    setSelectedItems,
    setSelectedMaterials,
    setSelectedSegmentations,
  ]);

  // Update hasMore state when data changes
  useEffect(() => {
    if (data) {
      const total = data.aggregateUser._count?._all || 0;
      setHasMore(data.users.length < total);
    }
  }, [data, setHasMore]);

  // Local search term with debounce
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);

  const handleSearchChange = useCallback(
    (value: string) => {
      setLocalSearchTerm(value);
      
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      
      searchDebounceRef.current = setTimeout(() => {
        setSearchTerm(value);
      }, SEARCH_DEBOUNCE);
    },
    [setSearchTerm, SEARCH_DEBOUNCE]
  );

  // Optimized refetch with query comparison
  const safeRefetch = useCallback(async () => {
    const currentQuery = JSON.stringify(query);
    
    // Éviter les refetch inutiles si la query n'a pas changé
    if (currentQuery === lastQueryRef.current) return;
    
    lastQueryRef.current = currentQuery;

    try {
      if (openModalSearch) {
        await refetch();
      }
    } catch (error) {
      console.error("Refetch failed:", error);
    }
  }, [query, openModalSearch, refetch]);

  // Initial load - optimisé pour charger une seule fois
  useEffect(() => {
    if (openModalSearch && !data) {
      getCraftmans();
    }
  }, [openModalSearch, getCraftmans, data]);

  // Refetch only when query changes significantly
  useEffect(() => {
    if (searchTerm || countFilter > 0) {
      safeRefetch();
    }
  }, [safeRefetch, searchTerm, countFilter]);

  // Reset state when modal closes (avec cleanup)
  useEffect(() => {
    if (!openModalSearch) {
      // Sauvegarder la position de scroll
      scrollPositionRef.current = 0;
      
      clearFilters();
      setSearchTerm("");
      setLocalSearchTerm("");
      lastQueryRef.current = "";
    }
  }, [clearFilters, openModalSearch, setSearchTerm]);

  // Filter users with photos (optimisé)
  const usersWithPhotos = useMemo(() => {
    if (!data?.users) return [];
    return data.users.filter((user) => user?.PhotoAchievements?.length > 0);
  }, [data?.users]);

  // Optimized load more function avec throttling
  const loadMoreCraftmans = useCallback(() => {
    if (loadingRef.current || loading || !hasMore || loadingMore) return;

    loadingRef.current = true;
    setLoadingMore(true);

    const currentLength = data?.users.length || 0;

    if (loadMoreDebounceRef.current) {
      clearTimeout(loadMoreDebounceRef.current);
    }

    loadMoreDebounceRef.current = setTimeout(async () => {
      try {
        await fetchMore({
          variables: {
            ...orderBy,
            skip: currentLength,
            take: size,
            where: {
              ...query,
              role: { equals: Role.Craftman },
              active: { equals: true },
            },
            aggregateUserWhere2: {
              ...query,
            },
          },
        });

        const total = data?.aggregateUser._count?._all || 0;
        setHasMore(currentLength + size < total);
      } catch (err) {
        console.error("Failed to load more data:", err);
        setHasMore(false);
      } finally {
        loadingRef.current = false;
        setLoadingMore(false);
      }
    }, DEBOUNCE_DELAY);
  }, [
    loading,
    hasMore,
    loadingMore,
    data?.users.length,
    data?.aggregateUser._count?._all,
    fetchMore,
    orderBy,
    size,
    query,
    setHasMore,
    setLoadingMore,
    DEBOUNCE_DELAY,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
      if (loadMoreDebounceRef.current) {
        clearTimeout(loadMoreDebounceRef.current);
      }
    };
  }, []);

  // Memoized empty state
  const EmptyState = useMemo(
    () => (
      <div className="text-center text-sm py-20 text-gray-500">
        {getText({ id: "no_result" })}
      </div>
    ),
    [getText]
  );

  // Memoized error state
  const ErrorState = useMemo(() => {
    if (!error) return null;
    return (
      <div className="text-center text-sm py-20 text-red-500">
        {error.message === "Failed to fetch"
          ? "Aucune connexion internet."
          : error.message}
      </div>
    );
  }, [error]);

  return (
    <DrawerDialog
      classNameWindow="max-w-2xl h-full md:max-h-[100dvh] w-full flex items-center flex-col"
      title={getText({ id: "search_craftman" })}
      open={openModalSearch}
      setOpen={setOpenModalSearch}
      isDesktopForAllScreen
    >
      {/* Header with search input */}
      <div className="sticky top-0 flex flex-col items-end space-y-3 bg-white w-full pb-3 md:pb-4 z-50 px-4 md:px-4 pt-4 md:pt-5 border-b border-gray-100">
        <InputSearch
          value={localSearchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
        <div className="w-full flex items-center justify-between">
          <span
            className={`${
              isMobileXs ? "text-xs" : "text-sm"
            } text-gray-600 font-medium`}
          >
            {`${countFilter} ${
              countFilter > 1
                ? getText({ id: "filtersApplied" })
                : getText({ id: "filterApplied" })
            }`}
          </span>
        </div>
        <PopoverFilter />
      </div>

      {/* Scrollable content */}
      <div
        className={`flex-1 w-full overflow-y-auto overflow-x-hidden px-4 md:px-4 ${
          isIos ? "overflow-scrolling-touch" : ""
        }`}
        style={{
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
          willChange: "scroll-position",
        }}
      >
        <div className="py-4 space-y-4">
          {usersWithPhotos.map((user) => (
            <div
              className="w-full flex flex-col items-center justify-center"
              key={user.id}
            >
              <MemoizedPostCard user={user} />
            </div>
          ))}

          {/* Infinite scroll trigger */}
          {hasMore && !error && (
            <InView
              as="div"
              className="w-full flex justify-center py-4"
              onChange={(inView) => {
                if (inView) {
                  loadMoreCraftmans();
                }
              }}
              threshold={LOAD_MORE_THRESHOLD}
              triggerOnce={false}
              skip={loading || loadingMore}
            >
              {(loadingMore || loading) && <SkeletonPost />}
            </InView>
          )}

          {/* Empty state */}
          {!loading && !loadingMore && usersWithPhotos.length === 0 && !error && EmptyState}

          {/* Error state */}
          {ErrorState}
        </div>
      </div>
    </DrawerDialog>
  );
};

export default memo(ModalSearch);